import { v4 as uuid } from 'uuid';
import cloneDeep from 'lodash/cloneDeep';
import isNil from 'lodash/isNil';
import merge from 'lodash/merge';
import toString from 'lodash/toString';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import isDate from 'date-fns/isDate';

import { IImageFileSource, IImportColumn, IProductExcel, TImageField } from '../types';
import { fileToDataUrl } from 'utils/imgix';
import { getDataType, checkEnumType, getEnumValue, convertStringToBoolean, isValidEANValue } from './excel';
import IndexedDB from 'utils/indexedDB';
import { PHRASE_FILL_CODE_FIELDS, PHRASE_FILL_FIELDS } from 'constants/bulk-import';
import { sanitizePhraseFills } from './phraseFills';

import { TExcelDataRow } from 'models/excel';
import IProduct, { IOtherSubstance } from 'models/product';
import { IFoodlaCategoryOption } from 'models/category';
import IProducer from 'models/producer';
import {
  PRODUCT_EAN_FIELDS,
  PRODUCT_BOOLEAN_FIELD,
  PRODUCT_BOOLEAN_FIELD_VALUES,
  PRODUCT_DATE_FIELD,
  PRODUCT_ENUM_FIELD,
  PRODUCT_ENUM_FIELD_VALUES,
  PRODUCT_NUMBER_FIELD,
  NON_NEGATIVE_NUMBER_FIELDS,
  SUPPLEMENT_SUBSTANCE_UNITS,
} from 'components/constants-ts';
import { isNewFood } from 'components/product/ProductUpdateForm/fields/category';
import { processImage } from 'components/fileuploader/ImageUploader2';
import { getBrand, getBrandFood } from 'components/product/ProductUpdateForm/utils';
import { calculateTax, isTaxRecalculationEvent } from 'components/product/ProductUpdateForm/fields/basic-data';
import {
  DB_NAME,
  SAVED_FILE_ID,
  SAVED_FILE_DATA_ID,
  SAVED_SHEET_SELECTION,
  OBJECT_STORAGE_NAME,
  SAVED_PRODUCT_IMAGE,
  ALLOWED_IMAGE_FILE_TYPE_LIST,
  ALLOWED_RECOGNIZE_IMAGE_FILE_TYPE_LIST,
  PARSE_IMAGE_BY,
  BOOLEAN_CHOICE_FIELDS,
  LEGACY_QUALITY_CODE_FIELDS,
  COLUMN_DETAILS,
} from '../constants';
import { getCountryCode, getCountryName, isBlank } from 'utils/helpers-ts';

export const copyFile = async (file: File): Promise<File> => {
  return new File([await file.arrayBuffer()], file.name, {
    type: file.type,
    lastModified: file.lastModified,
  });
};

export const parseFileName = (fileName: string) => {
  const splitedName = fileName.split('.');
  let extension = splitedName.pop() || '';
  const name = splitedName.join('.');

  return { name, extension };
};

export const parseImageFile = async (params: {
  file: File;
  token: string;
  startCallback?: () => void;
  addWatermark?: boolean;
  product?: IProduct;
  producer?: IProducer;
  field?: TImageField;
}): Promise<IImageFileSource> => {
  const { file, token, startCallback, addWatermark, product, producer } = params || {};

  let fileName = file.name || '';
  let { name, extension } = parseFileName(fileName);
  let convertedFile = file;

  startCallback?.();
  const { file: newConvertedFile } = await processImage(file, token, addWatermark, product, producer);
  if (newConvertedFile) {
    convertedFile = newConvertedFile;
    fileName = newConvertedFile.name;
    if (newConvertedFile.type === 'image/png') {
      extension = 'png';
    }
  }

  const source = await fileToDataUrl(convertedFile);
  return { id: uuid(), source, fileName, name, extension, file, convertedFile, field: params.field || 'images' };
};

export const createIndededDB = async () => {
  const instance = new IndexedDB({
    databaseName: DB_NAME,
    objectStoreNameList: [OBJECT_STORAGE_NAME],
  });
  const { data: database } = await instance.openDatabase();
  return database;
};

export const readExcelDataFromIndexedDB = async (indexedDB: IndexedDB) => {
  if (!indexedDB) return { excelFile: null, excelFileData: [], hasSheetSelection: false };

  const [fileResponse, fileDataResponse, sheetSelectionResponse] = await Promise.all([
    indexedDB!.read<File>(OBJECT_STORAGE_NAME, SAVED_FILE_ID),
    indexedDB!.read<TExcelDataRow[]>(OBJECT_STORAGE_NAME, SAVED_FILE_DATA_ID),
    indexedDB!.read<unknown>(OBJECT_STORAGE_NAME, SAVED_SHEET_SELECTION),
  ]);

  return {
    excelFile: fileResponse?.data?.value || null,
    excelFileData: fileDataResponse?.data?.value || [],
    // Presence of the pick (not its value — it can be null) signals that
    // `parseAndStore` produced this slice, i.e. `excelFileData` is already
    // sliced to header-at-index-0. The caller pins `excelHeaderRowIndex` to 0
    // so `parseExcelDataToProduct` skips re-detection (which could re-slice an
    // already-sliced sheet). Absent for pre-feature rows → caller keeps null.
    hasSheetSelection: !!sheetSelectionResponse?.data,
  };
};

export const readImageFileDataFromIndexedDB = async (indexedDB: IndexedDB): Promise<IImageFileSource[]> => {
  if (!indexedDB) return [];

  try {
    const { data } = await indexedDB.read<IImageFileSource[]>(OBJECT_STORAGE_NAME, SAVED_PRODUCT_IMAGE);
    return (data?.value || []).filter(({ url }) => !!url);
  } catch {
    return [];
  }
};

export const writeImageFileDataFromIndexedDB = async (
  indexedDB: IndexedDB,
  imageFileDataList: IImageFileSource[]
): Promise<void> => {
  if (!indexedDB) return;

  try {
    await deleteImageFileDataFromIndexedDB(indexedDB);
    await indexedDB.write<IImageFileSource[]>(OBJECT_STORAGE_NAME, {
      id: SAVED_PRODUCT_IMAGE,
      value: imageFileDataList
        .filter(({ url }) => !!url)
        .map(({ file, convertedFile, source, ...fileData }) => ({
          ...fileData,
          source: '',
          generateAiError: '',
          uploadAiError: '',
          uploadError: '',
        })),
    });
  } catch {}
};

export const deleteImageFileDataFromIndexedDB = async (indexedDB: IndexedDB): Promise<void> => {
  if (!indexedDB) return;

  try {
    await indexedDB.delete(OBJECT_STORAGE_NAME, SAVED_PRODUCT_IMAGE);
  } catch {}
};

export const filterAllowedImages = (files: File[], field: TImageField = 'images') => {
  const allowedList = field === 'images' ? ALLOWED_IMAGE_FILE_TYPE_LIST : ALLOWED_RECOGNIZE_IMAGE_FILE_TYPE_LIST;
  return files.filter(file => allowedList.includes(file.type));
};

export const testImageRegexString = (regex: string, parseBy: keyof IProduct, value: string) => {
  if (!regex || !value) return false;

  let regexStr = `^${regex}$`;
  if (parseBy === PARSE_IMAGE_BY.ARTICLE) regexStr = `^${regex}`;

  const [checkingValue] = value.split('_') || [''];

  try {
    if (regexStr && new RegExp(regexStr).test(checkingValue)) {
      return true;
    }
  } catch {
    return false;
  }
  return false;
};

export const applyImportProductValueRules = (
  product: IProductExcel,
  rootCategory: IFoodlaCategoryOption,
  { fillMissingTax = true }: { fillMissingTax?: boolean } = {}
): IProductExcel => {
  // basicDataTaxClassification: a supplied VAT is kept; saving a draft does not answer a missing one.
  if (fillMissingTax && isBlank(product.basicDataTaxClassification)) {
    product.basicDataTaxClassification = calculateTax(rootCategory, product.foodlaCategory, product.alcoholPercentage);
  }

  // flammableSubtype
  const hazardSymbols = getEnumValue(
    { field: 'hazardSymbols', isEnumType: true } as IImportColumn,
    product,
  );
  if (isBlank(product.flammableSubtype) || !hazardSymbols?.includes('FLAMMABLE')) {
    product.flammableSubtype = null;
  }
  return product;
};

// A review edit that is a native VAT event recalculates VAT; the result is saved like a user edit.
export const recalculateRowTax = (
  before: IProductExcel,
  after: IProductExcel,
  rootCategory: IFoodlaCategoryOption
): IProductExcel => {
  if (!isTaxRecalculationEvent(rootCategory, before, after)) return after;
  const basicDataTaxClassification = calculateTax(rootCategory, after.foodlaCategory, after.alcoholPercentage);
  return { ...after, basicDataTaxClassification, _editingData: { ...after._editingData, basicDataTaxClassification } };
};

export const mapUploadProductList = (
  productList: IProduct[],
  options: { rootCategory: IFoodlaCategoryOption; producer: IProducer; preserveUnanswered?: boolean }
) => {
  const { rootCategory, producer, preserveUnanswered } = options || {};
  if (!productList?.length) return [];

  let newProductList = cloneDeep(productList);

  return newProductList.map(product => {
    const newProduct = cloneDeep(product);
    const isFood = isNewFood(newProduct.foodlaCategory, rootCategory);

    // foodlaCategory
    if (newProduct.foodlaCategory) {
      const { id, name } = newProduct.foodlaCategory;
      newProduct.foodlaCategory = { id, name };
    }

    // country_of_manufacturing_string
    if (newProduct.country_of_manufacturing_string && newProduct.country_of_manufacturing_string?.trim() !== '') {
      const countryCode = getCountryCode(newProduct.country_of_manufacturing_string);
      if (countryCode) {
        newProduct.country_of_manufacturing = countryCode;
      } else {
        if (newProduct.country_of_manufacturing_string === 'Beligen') {
          newProduct.country_of_manufacturing = 56;
        } else if (newProduct.country_of_manufacturing_string === 'China') {
          newProduct.country_of_manufacturing = 156;
        } else if (newProduct.country_of_manufacturing_string === 'Holland') {
          newProduct.country_of_manufacturing = 528;
        }
      }
    }
    delete newProduct.country_of_manufacturing_string;

    // transportMinTemperature, transportMaxTemperature
    if (isNil(newProduct.transportMinTemperature)) {
      newProduct.transportMinTemperature = newProduct.min_temperature;
    }
    if (isNil(newProduct.transportMaxTemperature)) {
      newProduct.transportMaxTemperature = newProduct.max_temperature;
    }

    // brand, brand_food
    const brand = isFood ? getBrandFood(newProduct, producer) : getBrand(newProduct, producer);
    newProduct.brand_food = brand;
    newProduct.brand = brand;

    // EAN fields
    PRODUCT_EAN_FIELDS.forEach(eanField => {
      if (preserveUnanswered) {
        (newProduct[eanField] as any) = toString(product[eanField]).trim();
        return;
      }
      (newProduct[eanField] as any) = undefined;
      if (isValidEANValue({ field: eanField }, product)) {
        (newProduct[eanField] as any) = toString(product[eanField]);
      }
    });

    // article
    newProduct.article = toString(product.article);

    // purchasingDataSupplierArticleNo
    newProduct.purchasingDataSupplierArticleNo = toString(product.purchasingDataSupplierArticleNo);

    // responsibleLabelingApprovalNumber
    newProduct.responsibleLabelingApprovalNumber = toString(product.responsibleLabelingApprovalNumber);

    // date fields
    PRODUCT_DATE_FIELD.forEach(field => {
      if (preserveUnanswered) {
        const value = product[field];
        (newProduct[field] as any) = isDate(value) ? (value as Date).getTime() : value;
        return;
      }
      (newProduct[field] as any) = undefined;
      if (!product) return;

      const value = product[field] as Date;
      if (isDate(value)) {
        (newProduct[field] as any) = utcToZonedTime(value, 'Europe/Stockholm').getTime();
      }
    });

    // commodityCode
    if (!preserveUnanswered && !/^\d{8}$/.test(String(product.commodityCode?.trim()))) {
      newProduct.commodityCode = undefined;
    }

    // **** ENUM ****
    PRODUCT_ENUM_FIELD.forEach(field => {
      (newProduct[field] as any) = getEnumValue(
        {
          _id: '',
          isMandatory: true,
          title: '',
          field,
          dataType: 'string',
          isEnumType: true,
        },
        { ...newProduct, _excelData: {}, _prasedExcelData: {} },
        preserveUnanswered
      );
      // Keep unresolved source codes visible until the reviewer corrects them.
      if (preserveUnanswered && ['hazardStatements', 'securityNotices'].includes(field) &&
          isNil(newProduct[field]) && !isNil(product[field])) {
        (newProduct[field] as any) = Array.isArray(product[field]) ? product[field] : [product[field]];
      }
      if (preserveUnanswered && isNil(product[field])) (newProduct[field] as any) = null;
    });

    // phrase fills: only fills for the product's final codes reach the mutation
    PHRASE_FILL_FIELDS.forEach(field => {
      if (isNil(newProduct[field])) return;
      const codes = newProduct[PHRASE_FILL_CODE_FIELDS[field]];
      newProduct[field] = sanitizePhraseFills(newProduct[field], Array.isArray(codes) ? codes : []);
    });

    // enum: descriptive_size_unit
    if (
      newProduct.EAN?.startsWith('23') ||
      newProduct.EAN?.startsWith('2095') ||
      newProduct.EAN?.startsWith('2096') ||
      newProduct.EAN?.startsWith('2097') ||
      newProduct.EAN?.startsWith('2000')
    ) {
      newProduct.descriptive_size_unit = 'GRAM_APPROXIMATE';
    }

    // **** BOOLEAN ****
    // boolean fields
    PRODUCT_BOOLEAN_FIELD.forEach(field => {
      const isEnumType = checkEnumType(field);
      if (isEnumType) return;

      (newProduct[field] as any) = preserveUnanswered || BOOLEAN_CHOICE_FIELDS.includes(field) ? null : undefined;
      if (isEnumType || !product || isNil(product[field])) return;

      const convertedValue = convertStringToBoolean(String(product[field]), field);
      if (!isNil(convertedValue)) {
        (newProduct[field] as any) = convertedValue;
      }
    });

    // ceMarking fallback: when the bulk row carries no ceMarking value (old
    // Excel templates predating the explicit `ceMarking` column), derive from
    // `specifyCEStandards` to preserve backward-compat behavior. Explicit AI
    // and new-template producer values pass through PRODUCT_BOOLEAN_FIELD
    // conversion above and arrive non-null, so this branch is skipped.
    if (!preserveUnanswered && newProduct.ceMarking == null) {
      newProduct.ceMarking = !!newProduct.specifyCEStandards?.trim();
    }

    // number fields
    PRODUCT_NUMBER_FIELD.forEach(field => {
      if (product) {
        let value = product[field];
        if (!isNil(value) && typeof value !== 'number') {
          if (typeof value === 'string') {
            value = value.trim().replace(',', '.');
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const regex = /^([-+]?\d+(?:[,.]\d+)?)$/gimu;
            const matched = value.match(regex);
            if (matched?.[0] && matched[0].trim() !== '') {
              value = matched[0];
            } else {
              value = null;
            }
            if (value) {
              const parsedValue = parseFloat(value);

              if (!preserveUnanswered && NON_NEGATIVE_NUMBER_FIELDS.includes(field) && parsedValue < 0) {
                (newProduct[field] as any) = undefined;
              } else {
                (newProduct[field] as number) = parsedValue;
              }
            } else {
              (newProduct[field] as any) = preserveUnanswered ? product[field] : undefined;
            }
          }
        }
      }
    });

    applyImportProductValueRules(newProduct as IProductExcel, rootCategory, { fillMissingTax: !preserveUnanswered });

    // Rows are merged from the saved product; a stale legacy Boolean must not be sent with the code.
    Object.keys(LEGACY_QUALITY_CODE_FIELDS).forEach(field => {
      delete newProduct[field as keyof IProduct];
    });

    return newProduct;
  });
};

export const reorderImages = (imageList: IImageFileSource[]) => {
  const mainImage = imageList.find(image => image.field === 'images' && image.name.endsWith('AIGENERATED') && image.url);
  return mainImage ? [mainImage, ...imageList.filter(image => image.id !== mainImage.id)] : imageList;
};

export const getImageDataFileName = (imageData?: IImageFileSource) => {
  if (!imageData) return '';
  if (!imageData?.url) return imageData?.name || '';
  const [, ...names] = imageData?.name?.split('_') || [];
  return names.join('_');
};

export const sortImagesByName = (imageDataList: IImageFileSource[]) => {
  const result = [...imageDataList];
  result.sort((prev, next) => {
    const prevName = getImageDataFileName(prev);
    const nextName = getImageDataFileName(next);
    const prevOrder = prevName?.split('_')?.[1] || -Infinity;
    const nextOrder = nextName?.split('_')?.[1] || -Infinity;

    if (isNaN(Number(prevOrder)) && isNaN(Number(nextOrder))) return 0;
    if (isNaN(Number(prevOrder))) return 1;
    if (isNaN(Number(nextOrder))) return -1;
    return Number(prevOrder) - Number(nextOrder);
  });
  return reorderImages(result);
};

export const getImageDataListToUpload = (
  productImageDataSet: Record<string, IImageFileSource[]>,
  dataList: IProductExcel[]
) => {
  const uploadingImageDataList: IImageFileSource[] = [];

  Object.entries(productImageDataSet).forEach(([productId, imageDataList]) => {
    const product = dataList.find(({ id }) => id === productId);
    if (!product) return;

    // [!name.endsWith('AIGENERATED')]: remove ai image
    const sortedImageDataList = sortImagesByName(imageDataList);
    const filteredImageDataList = sortedImageDataList.filter(({ url, name }) => !url && !name.endsWith('AIGENERATED'));

    filteredImageDataList.forEach((imageData, index) => {
      // original image was uploaded, don't modify it
      if (imageData.url) {
        uploadingImageDataList.push(imageData);
        return;
      }

      let name = `${Math.ceil(Date.now() / 1000)}_${product.EAN || product.EAN2 || ''}`;
      if (filteredImageDataList.length > 1) name += `_${index + 1}`;
      const extension = imageData.extension;
      const fileName = `${name}.${extension}`;
      uploadingImageDataList.push({ ...imageData, assignProductId: product.id, name, fileName });
    });
  });

  return uploadingImageDataList;
};

export const revertProduct = (product: IProduct) => {
  const convertedSavedProduct = cloneDeep(product);

  Object.entries(convertedSavedProduct).forEach(entry => {
    const [field, value] = entry as [keyof IProduct, any];
    const dataType = getDataType(field);
    const isEnumType = checkEnumType(field);
    if (dataType === 'boolean' && !isEnumType) {
      if (isNil(value)) return;
      const booleanFieldValue = PRODUCT_BOOLEAN_FIELD_VALUES[field]?.find(item => item.convertTo === value);
      if (booleanFieldValue) {
        (convertedSavedProduct[field] as string) = booleanFieldValue.valueList[0];
      } else {
        (convertedSavedProduct[field] as string) = value ? 'Yes' : 'No';
      }
    }
    if (dataType === 'date' && !isNil(value)) {
      (convertedSavedProduct[field] as Date) = new Date(value);
    }
    if (checkEnumType(field)) {
      const enumFieldValue = PRODUCT_ENUM_FIELD_VALUES[field]?.find(item => item.convertTo === value);
      if (enumFieldValue) {
        (convertedSavedProduct[field] as string) = enumFieldValue.value;
      }
    }
  });

  // ItemCategoriesOnline
  const productNotes = (convertedSavedProduct as any).productNotes as {
    fieldName: string;
    value: (string | number)[];
  }[];
  const productNote = productNotes.find(({ fieldName }) => fieldName === 'ItemCategoriesOnline');
  if (productNote?.value?.length) {
    convertedSavedProduct.ItemCategoriesOnline = toString(productNote.value[0]);
  }

  // country_of_manufacturing
  if (convertedSavedProduct.country_of_manufacturing) {
    convertedSavedProduct.country_of_manufacturing_string =
      getCountryName(convertedSavedProduct.country_of_manufacturing) || undefined;
  }

  return convertedSavedProduct;
};

// Row values win over the saved product. Supplied lists replace the saved ones;
// [_.merge] would combine the two arrays by index.
export const mergeRowIntoSavedProduct = (savedProduct: IProduct, row: Partial<IProductExcel>): IProductExcel => {
  // [_.merge] mutates its target, so merge into a copy
  const mergedData = merge(cloneDeep(savedProduct), row) as IProductExcel;
  [...PHRASE_FILL_FIELDS, 'other_substances' as const, 'hazardStatements' as const, 'securityNotices' as const].forEach(field => {
    if (!isNil(row[field])) (mergedData[field] as any) = row[field];
  });
  return mergedData;
};

// FC-2037: an empty "Näringsenhet" cell must not replace the saved daily dose basis
// with the column default, or the saved daily dose is cleared on save.
const KEEP_SAVED_ON_EMPTY_CELL_FIELDS: (keyof IProduct)[] = ['nutritional_unit'];

export const restoreSavedDefaultValues = (
  mergedData: IProductExcel,
  row: IProductExcel,
  savedProduct: IProduct,
  excelColumnList: IImportColumn[]
) => {
  COLUMN_DETAILS.forEach(columnDetail => {
    const hasDefaultValue = !isNil(columnDetail.defaultValue);

    const isEmptyCellKeepingSavedValue =
      KEEP_SAVED_ON_EMPTY_CELL_FIELDS.includes(columnDetail.field) && isBlank(row._prasedExcelData?.[columnDetail.field]);

    const isImported =
      !isNil(mergedData[columnDetail.field]) &&
      !isEmptyCellKeepingSavedValue &&
      excelColumnList.some(({ isExtra, field }) => !isExtra && field === columnDetail.field);

    // check if value is [defaultValue]
    if (isImported || !hasDefaultValue) return;

    if (!isNil(savedProduct[columnDetail.field])) {
      // replace [defaultValue] by saved value
      (mergedData[columnDetail.field] as any) = savedProduct[columnDetail.field];
    }
  });

  // A missing VAT is filled on upload; an existing product keeps its saved VAT until a review edit recalculates it.
  if (
    isBlank(row._prasedExcelData?.basicDataTaxClassification) &&
    isNil(row._editingData?.basicDataTaxClassification) &&
    !isNil(savedProduct.basicDataTaxClassification)
  ) {
    mergedData.basicDataTaxClassification = savedProduct.basicDataTaxClassification;
  }
};

export const parseOtherSubstances = (value: string): IOtherSubstance[] => {
  const units = SUPPLEMENT_SUBSTANCE_UNITS.map(({ value }) => value)
    .sort((a, b) => b.length - a.length);

  return value.replace(/^"|"$/g, '').split(',').flatMap(item => {
    let name = item.trim();
    const unit = units.find(unit =>
      name.endsWith(unit) &&
      /(^|[\d\s])$/.test(name.slice(0, -unit.length))
    );

    if (unit) name = name.slice(0, -unit.length).trim();

    const match = name.match(/(?:^|\s)(\d+(?:\.\d+)?)$/);
    const amount = match ? Number(match[1]) : undefined;

    if (match) name = name.slice(0, match.index).trim();

    return name || !isNil(amount) || unit
      ? [{ name, amount, unit }]
      : [];
  });
}
